import React, { useState, useRef, useEffect } from 'react';

import { get, set, capitalize } from 'lodash';
import { patch, post } from 'helpers/api';

import Button from 'shared/button';
import Toggle from 'components/toggle';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';

import DemographicsMap from './demographics_map';
import UnsavedDataGuard from './unsaved_data_guard';
import { demographicsFieldDefinitions } from './demographics_constants';
import { showSnackbar } from 'components/ui/snackbar/helper';

import './styles.scss';

type DemographicsReportConfig = {
  categories: Record<string, boolean>;
  company: {
    id: number;
    name: string,
    primaryColor: string;
  },
  configId: number;
  demographicsReportId: number;
  demographicsData: Record<string, any>[];
  hiddenFields: Set<string>;
  overrideData?: Record<string, any>[];
  overrideValues?: boolean;
  preview: boolean;
  lastRefreshedAt?: string | null;
  sourceHtml?: string | null;
  property: {
    id: number;
    name: string;
    address: string;
    latitude: number;
    longitude: number;
  },
  radiusRings: number[];
  savePath: string;
  showDriveTime: boolean;
  showRadiusRings: boolean;
}

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60000;

function DemographicsTab(props: DemographicsReportConfig) {
  const uiMaxWidth = '80%';

  // Use BO colors on the 'edit screen' and then company colors on the customer facing
  const companyPrimaryColor = props.preview  ? props.company?.primaryColor : '#435674';
  const lastRefreshed = props.lastRefreshedAt ? new Date(props.lastRefreshedAt) : null;

  const allCategories = [
    { label: 'Overview', field: 'overview' },
    { label: 'Age', field: 'age' },
    { label: 'Gender', field: 'gender' },
    { label: 'Population', field: 'population' },
    { label: 'Education', field: 'education' },
    { label: 'Race & Ethnicity', field: 'ethnicity' },
    { label: 'Households', field: 'households' },
    { label: 'Labor Force', field: 'labor_force' },
    { label: 'Housing Units', field: 'housing_units' },
    { label: 'Marital Status', field: 'marital_status' },
    { label: 'Household Income', field: 'household_income' },
    { label: 'Travel Time to Work', field: 'travel_time_to_work' },
    { label: 'Types of Housing Units', field: 'types_of_housing_units' },
    { label: 'Population by Industry', field: 'population_by_industry' },
    { label: 'Transportation to Work', field: 'transportation_to_work' },
    { label: 'Population by Occupation', field: 'population_by_occupation' },
    { label: 'Value of Owner Occupied Housing Units', field: 'value_of_owner_occupied_housing_units' },
  ];

  const getCategoryLabel = (field: string) => {
    const category = allCategories.find(cat => cat.field === field);
    return category ? category.label : capitalize(field);
  };

  // helpers to convert data into snake_case from camelCase
  const toSnake = (key: string) => key.replace(/([A-Z])/g, '_$1').toLowerCase();
  const normalizeRings = (rings: any[]): Record<string, any>[] => Array.isArray(rings)
    ? rings.map((r) => {
      if (!r || typeof r !== 'object') return r;
      const out: Record<string, any> = {};
      Object.keys(r).forEach((k) => {
        out[toSnake(k)] = r[k];
      });
      return out;
    })
    : [];

  const [config, setConfig] = useState<DemographicsReportConfig>(props);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [activeInfoCategory, setActiveInfoCategory] = useState<string>('overview');
  const [saveStatus, setSaveStatus] = useState<'success' | 'failure' | null>(null);
  const [hiddenRows, setHiddenRows] = useState<Set<string>>(new Set(props.hiddenFields || []));
  const [useCustomValues, setUseCustomValues] = useState<boolean>(props.overrideValues || false);
  const [customValues, setCustomValues] =
    useState<Record<string, number>[]>(normalizeRings(props.overrideData || props.demographicsData || []));
  const [isRegenerating, setIsRegenerating] = useState<boolean>(false);
  const initialStateRef = useRef({
    radiusRings: props.radiusRings ? [...props.radiusRings] : [],
    hiddenRows: new Set(props.hiddenFields || []),
  });
  const [changedCustomValues, setChangedCustomValues] = useState<boolean>(false);
  const tableTopRef = useRef<HTMLDivElement>(null);
  const isDirty = (
    JSON.stringify(config.radiusRings) !== JSON.stringify(initialStateRef.current.radiusRings) ||
    JSON.stringify([...hiddenRows]) !== JSON.stringify([...initialStateRef.current.hiddenRows]) ||
    changedCustomValues
  );
  const radiusChangedRef = useRef<boolean>(false);

  const setRadiusRing = (index: number, value: number) => {
    const origRadii = get(config, 'radiusRings', []);
    const newRadii = [...origRadii];
    if (newRadii[index] === value) return;

    newRadii[index] = value;
    radiusChangedRef.current = true;
    applyConfigChange({
      ...config,
      radiusRings: newRadii
    });
  };

  const applyConfigChange = (newConfig: DemographicsReportConfig) => {
    setConfig(newConfig);
  };

  const autoSaveTimerRef = useRef<number | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearAutoSaveTimer = () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
  };

  const clearPollTimer = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const pollForGenerationComplete = () => {
    clearPollTimer();
    const reportId = config.demographicsReportId;
    const started = Date.now();

    showSnackbar({ text: 'Regenerating data…', variant: 'info' });

    const poll = async () => {
      if (Date.now() - started > POLL_TIMEOUT_MS) {
        showSnackbar({
          text: 'Data regeneration is taking longer than expected. Please reload the page manually.',
          variant: 'danger'
        });
        return;
      }
      try {
        const resp = await fetch(`/demographics_reports/${reportId}/status`);
        const json = await resp.json();
        if (json.status === 'ready' || json.status === 'generate_failed') {
          window.removeEventListener('beforeunload', onBeforeUnload);
          window.location.reload();
        } else {
          pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
      } catch {
        pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
  };

  useEffect(() => {
    return () => {
      clearAutoSaveTimer();
      clearPollTimer();
    };
  }, []);

  const handleSave = async (): Promise<boolean> => {
    setIsSaving(true);
    setSaveStatus(null);
    setChangedCustomValues(false);

    // Store if radius changed before async operations
    const radiusChanged = radiusChangedRef.current;

    const params = {
      config: {
        categories: config.categories,
        configId: config.configId,
        hiddenFields: Array.from(hiddenRows),
        showDriveTime: config.showDriveTime,
        showRadiusRings: config.showRadiusRings
      },
      demographics_report: {
        measurements: 'imperial',
        override_values: useCustomValues,
        override_data: useCustomValues ? customValues : null,
        radius1: config.radiusRings[0],
        radius2: config.radiusRings[1],
        radius3: config.radiusRings[2]
      }
    };

    try {
      const response = await patch(config.savePath, params);
      if (response && response.success === true) {
        initialStateRef.current = {
          radiusRings: [...config.radiusRings],
          hiddenRows: new Set(hiddenRows),
        };

        if (radiusChanged) {
          radiusChangedRef.current = false;
          pollForGenerationComplete();

          return true;
        } else {
          showSnackbar({ text: 'Saved successfully', variant: 'success' });
          return true;
        }
      } else {
        setSaveStatus('failure');
        return false;
      }
    } catch (e) {
      showSnackbar({ text: `An error occurred while saving. ${e}`, variant: 'danger' });
      setSaveStatus('failure');
      return false;
    } finally {
      setIsSaving(false);

      setTimeout(() => {
        setSaveStatus(null);
      }, 5000);
    }
  };

  const handleRegenerate = async () => {
    setIsRegenerating(true);

    try {
      const response = await post(`/demographics_reports/${config.demographicsReportId}/regenerate_data`, {});

      if (response && response.success === true) {
        window.location.reload();
      } else {
        showSnackbar({ text: 'Regeneration failed', variant: 'danger' });
      }
    } catch (e) {
      showSnackbar({ text: `Regeneration request failed. ${e}`, variant: 'danger' });
    } finally {
      setIsRegenerating(false);
    }
  };

  const saveButtonDisabled = () => {
    return isSaving || !isDirty;
  };

  const renderHeader = () => {
    return (
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <h5 style={{ color: 'rgb(28 29 33)', fontWeight: 'bold' }}>Demographics</h5>
          <p style={{ marginBottom: 0 }}>
            Last refreshed: {lastRefreshed ? lastRefreshed.toLocaleDateString() : '—'}
            <a
              aria-disabled={isRegenerating}
              href="#"
              style={{ cursor: isRegenerating ? 'not-allowed' : 'pointer', opacity: isRegenerating ? 0.6 : 1 }}
              onClick={(e) => {
                e.preventDefault();
                if (!isRegenerating) handleRegenerate();
              }}
            >
              {isRegenerating ? '. Refreshing…' : '. Refresh'}
            </a>
          </p>
        </div>
        <div className="d-flex align-items-center" style={{ gap: '1rem' }}>
          {saveStatus === 'success' &&
            <div className="text-success">Saved successfully</div>
          }
          {saveStatus === 'failure' &&
            <div className="text-danger" data-testid="failure-message">
              Save failed
            </div>
          }
          <Button.Primary
            data-testid="save-button"
            disabled={saveButtonDisabled()}
            style={{ fontWeight: 'normal' }}
            onClick={handleSave}
          >
            {isSaving ? 'Saving ...' : 'Save Changes'}
          </Button.Primary>
        </div>
      </div>
    );
  };

  const toggleCustomValues = () => {
    if (useCustomValues) {
      setCustomValues(normalizeRings(props.demographicsData || []));
    }
    setChangedCustomValues(true);
    setUseCustomValues(!useCustomValues);
  };

  const customValuesToggle = () => {
    return (
      <div className="d-flex">
        <span className={`me-3${useCustomValues ? ' inactive' : ''}`}>Default Values</span>
        <Toggle
          checked={useCustomValues}
          showText={false}
          theme="ds"
          onChange={toggleCustomValues}
        />
        <span className={`ms-3${useCustomValues ? '' : ' inactive'}`}>Custom Values</span>
      </div>
    );
  };

  const renderInfoCard = () => {
    const property = props.property;

    return (
      <div className="info-content">
        <DemographicsMap
          latitude={property.latitude}
          longitude={property.longitude}
          radiusColor={companyPrimaryColor}
          radiusRings={config.radiusRings}
          showRadiusRings={config.showRadiusRings}
          onRadiusRingChange={(i, v) => setRadiusRing(i, v)}
        />
        <div className="info-header">
          <span style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            alignSelf: 'stretch'
          }}
          >
            <h5
              style={{ color: 'rgb(28 29 33)', fontWeight: 'bold', fontSize: '24px', margin: 0 }}
            >
              {getCategoryLabel(activeInfoCategory)}
            </h5>
            {customValuesToggle()}
          </span>
          <p>Rows hidden will also be hidden in documents.</p>
        </div>
        <div className="info-body">
          { renderSidebar()}
          { infoTable()}
        </div>
      </div>
    );
  };

  const infoTable = () => {
    return (
      <div className="info-table">
        <div className="info-container d-flex flex-column">
          <div className="info-data">
            { infoDataTable() }
          </div>
        </div>
      </div>
    );
  };

  const infoDataTable = () => {
    // Normalize incoming demographic ring data to snake_case
    const rawData = props.demographicsData || [];
    const data = normalizeRings(rawData);
    if (!data || data.length === 0) {
      return <p className="p-3">No data available.</p>;
    }

    const allDataFields = demographicsFieldDefinitions;

    // For Overview tab, show only fields marked for overview
    const fieldsToRender = (() => {
      if (activeInfoCategory.toLowerCase() === 'overview') {
        return allDataFields.filter(field => field.showInOverview === true);
      }
      return allDataFields.filter(field => field.category === activeInfoCategory);
    })();


    const formatValue = (value, format) => {
      if (value === null || typeof value === 'undefined') return 'N/A';

      switch (format) {
      case 'currency':
        return `$${Math.round(value).toLocaleString()}`;
      case 'number':
        return value.toLocaleString();
      case 'decimal':
        return value.toFixed(1);
      default:
        return value;
      }
    };

    const defaultValues = (ring, index, field, dataKey) => {
      const value = get(ring, dataKey);
      return (
        <td
          className="data-cell align-middle"
          key={index}
          style={{ textAlign: 'center' }}
        >
          {formatValue(value, field.format)}
        </td>
      );
    };

    const editableValues = (ring, index, field, dataKey) => {
      return (
        <td
          className="data-cell align-middle"
          key={index}
          style={{ textAlign: 'center' }}
        >
          <input
            style={{ width: '100%' }}
            type="number"
            value={get(customValues[index], dataKey, '')}
            onChange={(e) => {
              setChangedCustomValues(true);
              const newValue = e.target.value === '' ? null : Number(e.target.value);
              setCustomValues((prev) => {
                const newCustomValues = [...prev];
                set(newCustomValues[index], dataKey, newValue);
                return newCustomValues;
              });
            }}
          />
        </td>
      );
    };

    return (
      <div className="info-data-table" ref={tableTopRef}>
        <table className="table table-striped">
          <thead>
            <tr>
              <th className="align-middle" style={{ backgroundColor: 'rgb(238, 238, 242)' }}></th>
              {data.map((ring, index) => (
                <th
                  className="align-middle"
                  key={index}
                  style={{ textAlign: 'center', backgroundColor: 'rgb(238, 238, 242)', whiteSpace: 'nowrap' }}
                >
                  {ring.radius_in_miles} Mile{ring.radius_in_miles !== 1 ? 's' : ''}
                </th>
              ))}
              <th className="align-middle" style={{ width: '30px', height: '46px', backgroundColor: 'rgb(238, 238, 242)' }}></th>
            </tr>
          </thead>
          <tbody>
            {fieldsToRender.map((field) => {
              const isHidden = hiddenRows.has(field.key);
              // for dupe rows (total population)
              const dataKey = field.dataKey || field.key;

              return (
                <tr className={isHidden ? 'row-hidden' : ''} key={field.key}>
                  <td
                    className="description-cell align-middle"
                    title={field.label}
                  >
                    {field.label}
                  </td>
                  {data.map((ring, index) => {
                    if (useCustomValues) {
                      return editableValues(ring, index, field, dataKey);
                    } else {
                      return defaultValues(ring, index, field, dataKey);
                    }
                  })}
                  <td className="text-center">
                    <FontAwesomeIcon
                      className="visibility-toggle"
                      icon={isHidden ? faEyeSlash : faEye}
                      style={{ cursor: 'pointer', width: '2em', fontSize: '1.5em' }}
                      title={isHidden ? 'Click to show this row' : 'Click to hide this row'}
                      onClick={() => toggleRowVisibility(field.key)}
                    />
                  </td>
                </tr>
              );
            })}
            {hiddenRows.size > 0 && (
              <tr className="hidden-rows-summary">
                <td className="text-center" colSpan={data.length + 2}>
                  <div className="mt-2 mb-2">
                    <span>{hiddenRows.size} row{hiddenRows.size !== 1 ? 's' : ''} hidden</span>
                    <button
                      className="btn btn-sm btn-link"
                      onClick={() => setHiddenRows(new Set())}
                    >
                      Show all rows
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSidebar = () => {
    const navItems = allCategories;

    return (
      <div className="info-sidebar">
        <div className="categories-list">
          {navItems.map(cat => (
            <div
              className="category-row"
              key={cat.field}
              onClick={() => setActiveInfoCategory(cat.field)}
            >
              <div className="d-flex align-items-center">
                <span className={`category-radio ${activeInfoCategory === cat.field ? 'checked' : ''}`} />
                <span className="category-label">{cat.label}</span>
              </div>
              <FontAwesomeIcon icon={faChevronRight} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Make sure the onBeforeUnload function is defined outside the useEffect so we can reference it
  const onBeforeUnload = (e: BeforeUnloadEvent) => {
    if (isDirty) {
      e.preventDefault();
      e.returnValue = ''; // Required for Chrome
    }
  };

  useEffect(() => {
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const toggleRowVisibility = (rowKey: string) => {
    setHiddenRows((prevHidden) => {
      const newHidden = new Set(prevHidden);
      if (newHidden.has(rowKey)) {
        newHidden.delete(rowKey);
      } else {
        newHidden.add(rowKey);
      }
      return newHidden;
    });
  };

  return (
    <div className="d-flex flex-column align-items-center">
      <UnsavedDataGuard
        message="You have unsaved changes. Are you sure you want to leave this page?"
        when={isDirty}
      />
      <div className="d-flex flex-column" style={{ gap: '2rem', width: '100%', maxWidth: uiMaxWidth }}>
        { renderHeader() }
        { renderInfoCard() }
      </div>
      <div className="text-center" style={{ color: '#9AA0A6', fontSize: '12px', marginTop: '10px' }}>
        <span dangerouslySetInnerHTML={{ __html: props.sourceHtml || '' }} />
      </div>
    </div>
  );
}

export default DemographicsTab;
